import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Calendar } from "../../../ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Label } from "../../../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import { Switch } from "../../../ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Textarea } from "../../../ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/tooltip";
import type { OfficeMapping, OfficePatch } from "../lib/api/office-mapping";
import type { OfficeAuditEntry } from "../lib/api/types";
import { cn } from "../lib/utils";

function fmtDate(value: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : format(d, "MMM d, yyyy");
}

function fmtStamp(value: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : format(d, "MMM d, yyyy · HH:mm");
}

/* ---------------------------------- Table --------------------------------- */

const helper = createColumnHelper<OfficeMapping>();

export function OfficesTable({ rows, onEdit }: { rows: OfficeMapping[]; onEdit: (o: OfficeMapping) => void }) {
  const columns = [
    helper.accessor("officeName", {
      header: "Office Name",
      cell: (info) => <span className="font-medium">{info.getValue()}</span>,
    }),
    helper.accessor("officeNumbers", {
      header: "Office Numbers",
      cell: (info) => (
        <div className="flex flex-wrap gap-1">
          {info.getValue().map((n) => (
            <Badge key={n} variant="outline" className="font-mono text-[11px]">
              {n}
            </Badge>
          ))}
        </div>
      ),
    }),
    helper.accessor("liveOnAthena", {
      header: "Live on Athena",
      cell: (info) =>
        info.getValue() ? (
          <Badge className="bg-amber-dim text-amber border border-amber/40">Live</Badge>
        ) : (
          <Badge className="bg-neutral-inactive-dim text-neutral-inactive border border-neutral-inactive/40">
            Not yet live
          </Badge>
        ),
    }),
    helper.accessor("goLiveDate", {
      header: "Go-Live Date",
      cell: (info) => (
        <span className={cn(!info.row.original.liveOnAthena && "text-neutral-inactive")}>
          {info.row.original.liveOnAthena ? fmtDate(info.getValue()) : "—"}
        </span>
      ),
    }),
    helper.accessor("note", {
      header: "Note",
      cell: (info) => {
        const note = info.getValue();
        if (!note) return <span className="text-muted-foreground">—</span>;
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block max-w-[220px] truncate text-muted-foreground">{note}</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{note}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    }),
    helper.display({
      id: "lastUpdated",
      header: "Last Updated",
      cell: ({ row }) =>
        row.original.lastUpdatedBy ? (
          <span className="text-xs text-muted-foreground">
            {row.original.lastUpdatedBy} · {fmtStamp(row.original.lastUpdatedAt)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    }),
    helper.display({
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <Button size="sm" variant="outline" onClick={() => onEdit(row.original)}>
          Edit
        </Button>
      ),
    }),
  ] as unknown as ColumnDef<OfficeMapping, any>[];

  const table = useReactTable({ data: rows, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => (
                <TableHead key={h.id} className="whitespace-nowrap text-xs uppercase tracking-wide">
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="align-top text-sm">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* --------------------------------- Dialog --------------------------------- */

export function RolloutDialog({
  office,
  open,
  onOpenChange,
  actingAs,
  onSave,
}: {
  office: OfficeMapping | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actingAs: string;
  onSave: (officeName: string, patch: OfficePatch) => void;
}) {
  const [live, setLive] = useState(false);
  const [goLiveDate, setGoLiveDate] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!office) return;
    setLive(office.liveOnAthena);
    setGoLiveDate(office.goLiveDate);
    setNote(office.note ?? "");
    setError(null);
  }, [office, open]);

  if (!office) return null;

  const turningOff = office.liveOnAthena && !live;
  const selected = goLiveDate ? new Date(goLiveDate) : undefined;

  function save() {
    if (!actingAs.trim()) {
      setError("Enter who you're acting as before saving.");
      return;
    }
    if (live && !goLiveDate) {
      setError("A go-live date is required when an office is live on Athena.");
      return;
    }
    onSave(office!.officeName, {
      liveOnAthena: live,
      goLiveDate,
      note: note.trim() === "" ? null : note.trim(),
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Update rollout status — {office.officeName}</DialogTitle>
          <DialogDescription>
            Direct edit — no approval queue. Every changed field is written to the audit log.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <Label className="text-sm">Live on Athena</Label>
              <p className="text-xs text-muted-foreground">
                Controls whether post-go-live data appears for this office.
              </p>
            </div>
            <Switch checked={live} onCheckedChange={setLive} aria-label="Live on Athena" />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Go-Live Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={!live}
                  className={cn(
                    "w-full justify-start gap-2 font-normal",
                    !goLiveDate && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="size-4" />
                  {goLiveDate ? fmtDate(goLiveDate) : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selected}
                  onSelect={(d) => setGoLiveDate(d ? format(d, "yyyy-MM-dd") : null)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {!live ? (
              <p className="text-xs text-neutral-inactive">
                Stored date is preserved while this office is not live.
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          </div>

          {turningOff ? (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-foreground">
              Turning this off will make {office.officeName}'s post-go-live data unavailable across
              every page until it's turned back on.
            </p>
          ) : null}

          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save}>Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------- Audit log ------------------------------- */

const auditHelper = createColumnHelper<OfficeAuditEntry>();

const FIELD_LABEL: Record<OfficeAuditEntry["fieldChanged"], string> = {
  liveOnAthena: "Live on Athena",
  goLiveDate: "Go-Live Date",
  note: "Note",
};

export function AuditLogTable({ entries }: { entries: OfficeAuditEntry[] }) {
  const columns = [
    auditHelper.accessor("changedAt", {
      header: "Timestamp",
      cell: (info) => <span className="whitespace-nowrap text-xs">{fmtStamp(info.getValue())}</span>,
    }),
    auditHelper.accessor("officeName", { header: "Office" }),
    auditHelper.accessor("fieldChanged", {
      header: "Field Changed",
      cell: (info) => (
        <Badge variant="outline" className="text-[11px]">
          {FIELD_LABEL[info.getValue()]}
        </Badge>
      ),
    }),
    auditHelper.display({
      id: "change",
      header: "Old Value → New Value",
      cell: ({ row }) => (
        <span className="text-sm">
          <span className="text-neutral-inactive">{row.original.oldValue}</span>
          <span className="px-2 text-muted-foreground">→</span>
          <span className="text-foreground">{row.original.newValue}</span>
        </span>
      ),
    }),
    auditHelper.accessor("changedBy", {
      header: "Changed By",
      cell: (info) => <span className="text-sm text-muted-foreground">{info.getValue()}</span>,
    }),
  ] as unknown as ColumnDef<OfficeAuditEntry, any>[];

  const data = [...entries].sort((a, b) => b.changedAt.localeCompare(a.changedAt));
  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => (
                <TableHead key={h.id} className="whitespace-nowrap text-xs uppercase tracking-wide">
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="text-sm">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
